import { useRef, useCallback, useState, useEffect } from "react";
import styled from "styled-components";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import theme from "../theme";

const GAP = 24;

const Wrapper = styled.div`
  position: relative;
  width: auto;
  margin: auto;
  padding: 0 4rem;
  min-height: ${(p) => p.$minHeight ?? "320px"};
  display: flex;
  align-items: center;
  max-width: 800px;

  ${theme.breakpoints.down("sm")} {
    padding: 0 3rem;
  }
`;

const Viewport = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  display: flex;
  gap: ${GAP}px;
  padding: 0.5rem 0;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  width: 100%;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const Item = styled.div`
  flex: 0 0 calc((100% - ${GAP * 2}px) / 3);
  min-width: 240px;
  scroll-snap-align: start;
  scroll-snap-stop: always;

  @media (max-width: 768px) {
    flex: 0 0 calc((100% - ${GAP}px) / 2);
    min-width: 200px;
  }

  @media (max-width: 642px) {
    flex: 0 0 100%;
    min-width: 180px;
  }
`;

const ArrowButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(p) => (p.$side === "left" ? "left: 0" : "right: 0")};
  z-index: 2;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  background: rgba(0, 0, 0, 0.4);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    opacity 0.2s ease,
    visibility 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  visibility: ${(p) => (p.$visible ? "visible" : "hidden")};
  pointer-events: ${(p) => (p.$visible ? "auto" : "none")};

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: var(--my-color5);
    color: var(--my-color5);
  }

  ${theme.breakpoints.down("sm")} {
    width: 40px;
    height: 40px;
  }
`;

const HorizontalCarousel = ({ children, minHeight = "320px" }) => {
  const viewportRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const threshold = 4;
    setCanScrollLeft(scrollLeft > threshold);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - threshold);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener("scroll", updateArrows);
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateArrows);
      ro.disconnect();
    };
  }, [updateArrows, children]);

  const scrollByItem = useCallback((direction) => {
    const el = viewportRef.current;
    if (!el) return;
    const item = el.querySelector("[data-carousel-item]");
    const itemWidth = item?.offsetWidth ?? 0;
    const scrollAmount = (itemWidth + GAP) * direction;
    el.scrollBy({ left: scrollAmount, behavior: "smooth" });
  }, []);

  const items = Array.isArray(children) ? children : [children];
  if (items.length === 0) return null;

  return (
    <Wrapper $minHeight={minHeight}>
      <ArrowButton
        type="button"
        $side="left"
        $visible={canScrollLeft}
        onClick={() => scrollByItem(-1)}
        aria-label="Précédent"
      >
        <FiChevronLeft size={24} />
      </ArrowButton>

      <Viewport ref={viewportRef}>
        {items.map((child, index) => (
          <Item key={index} data-carousel-item>
            {child}
          </Item>
        ))}
      </Viewport>

      <ArrowButton
        type="button"
        $side="right"
        $visible={canScrollRight}
        onClick={() => scrollByItem(1)}
        aria-label="Suivant"
      >
        <FiChevronRight size={24} />
      </ArrowButton>
    </Wrapper>
  );
};

export default HorizontalCarousel;
